import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';

/**
 * The store's locations and which members are assigned to them.
 *
 * A member with no locations works at every location, which is what every
 * membership carried before assignment existed. Owners and admins are never
 * scoped, so the API refuses to assign them.
 */

export interface StoreLocationSummary {
  id: string;
  name: string;
  isPrimary: boolean;
  isActive: boolean;
}

export interface StoreTeamMember {
  userId: string;
  email?: string | null;
  displayName?: string | null;
  role: string;
  status?: string;
  /** Empty means every location */
  locationIds?: string[];
}

const readError = async (response: Response, fallback: string): Promise<string> => {
  const body = await response.json().catch(() => ({}));
  return body?.error?.message || body?.message || fallback;
};

export const fetchStoreLocationOptions = async (): Promise<StoreLocationSummary[]> => {
  const response = await makeApiCall('/store/locations', { headers: await getAuthHeaders() });
  if (!response.ok) throw new Error(await readError(response, 'Failed to load locations'));
  return ((await response.json()).data || []) as StoreLocationSummary[];
};

export const fetchStoreTeamMembers = async (): Promise<StoreTeamMember[]> => {
  const response = await makeApiCall('/store/members', { headers: await getAuthHeaders() });
  if (!response.ok) throw new Error(await readError(response, 'Failed to load team members'));
  return ((await response.json()).data || []).map((member: any) => ({
    userId: member.userId || member.id,
    email: member.email ?? null,
    displayName: member.displayName ?? null,
    role: member.role || 'user',
    status: member.status || 'active',
    locationIds: Array.isArray(member.locationIds) ? member.locationIds : [],
  })) as StoreTeamMember[];
};

/** Assign a member to store locations; an empty list restores every location. */
export const updateMemberLocations = async (
  organizationId: string,
  userId: string,
  locationIds: string[],
): Promise<void> => {
  const response = await makeApiCall(`/organizations/${organizationId}/users/${userId}`, {
    method: 'PUT',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ locationIds }),
  });
  if (!response.ok) throw new Error(await readError(response, 'Failed to update stores'));
};
